/**
 * Campaign Detail Page
 *
 * Shows detailed information about a single campaign:
 * - View/edit campaign settings
 * - Display and regenerate API key
 * - View integration instructions
 * - Quick actions (pause, activate, delete)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { campaignService, getCampaignABStats } from '../../services/campaigns';
import type { Campaign } from '../../types/campaign';
import { ABTestStats } from '../../components/campaigns/ABTestStats';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';

export default function CampaignDetailPage() {
  const { t } = useTranslation('campaigns');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch campaign
  const fetchCampaign = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await campaignService.getCampaign(parseInt(id, 10));
      setCampaign(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('messages.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaign();
  }, [id]);

  // Regenerate API key
  const handleRegenerateApiKey = async () => {
    if (!id || !campaign) return;

    if (!confirm(t('detail.api_key.regenerate_confirm'))) {
      return;
    }

    try {
      const updated = await campaignService.regenerateApiKey(parseInt(id, 10));
      setCampaign(updated);
      toast.success(t('messages.api_key_regenerated'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('messages.api_key_regenerate_failed'));
    }
  };

  // Delete campaign
  const handleDelete = async () => {
    if (!id || !campaign) return;

    if (!confirm(t('detail.danger.delete_confirm', { name: campaign.name }))) {
      return;
    }

    try {
      await campaignService.deleteCampaign(parseInt(id, 10));
      toast.success(t('messages.deleted_success'));
      navigate('/campaigns');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('messages.delete_failed'));
    }
  };

  // Copy API key
  const handleCopyApiKey = () => {
    if (!campaign) return;
    navigator.clipboard.writeText(campaign.api_key);
    toast.success(t('messages.api_key_copied'));
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="space-y-6">
        <Alert variant="error">
          {error || t('messages.not_found')}
        </Alert>
        <button onClick={() => navigate('/campaigns')} className="btn btn-secondary">
          {t('detail.actions.back_to_list')}
        </button>
      </div>
    );
  }

  const isDeleted = !!campaign.deleted_at;

  return (
    <div className="space-y-6">
      {isDeleted && (
        <Alert variant="warning">
          {t('detail.deleted_banner')}
        </Alert>
      )}

      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
            <Badge variant={getStatusVariant(campaign.status)}>
              {t(`status.${campaign.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {t('detail.created', { date: new Date(campaign.created_at).toLocaleDateString() })}
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to={`/statistics?campaignId=${campaign.id}`}
            className="btn btn-secondary"
          >
            {t('detail.actions.view_statistics')}
          </Link>
          <Link
            to={`/campaigns/${campaign.id}/traffic-quality`}
            className="btn btn-secondary"
          >
            {t('detail.actions.traffic_quality')}
          </Link>
          <button onClick={() => navigate('/campaigns')} className="btn btn-secondary">
            {t('detail.actions.back_to_list')}
          </button>
        </div>
      </div>


      {/* Campaign Details */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('detail.details.title')}</h2>
          {!isDeleted && (
            <Link
              to={`/campaigns/${campaign.id}/edit`}
              className="btn btn-primary"
            >
              Edit Campaign
            </Link>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="label">{t('detail.details.name_label')}</label>
            <p className="text-sm text-gray-900">{campaign.name}</p>
          </div>

          <div>
            <label className="label">{t('detail.details.human_url_label')}</label>
            <p className="text-sm text-gray-900">{campaign.human_redirect_url}</p>
          </div>

          <div>
            <label className="label">{t('detail.details.bot_url_label')}</label>
            <p className="text-sm text-gray-900">{campaign.bot_redirect_url}</p>
          </div>

          {campaign.domains && campaign.domains.length > 0 && (
            <div>
              <label className="label">Campaign Domains</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {campaign.domains.map((domain, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-indigo-100 text-indigo-800 font-medium"
                  >
                    {typeof domain === 'string' ? domain : domain.domain}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label">{t('detail.details.status_label')}</label>
            <p className="text-sm text-gray-900">{t(`status.${campaign.status}`)}</p>
          </div>

          {campaign.assigned_manager && (
            <div>
              <label className="label">{t('detail.details.manager_label')}</label>
              <p className="text-sm text-gray-900">
                {campaign.assigned_manager.name} ({campaign.assigned_manager.email})
              </p>
            </div>
          )}

          {/* Traffic Filters Display */}
          {campaign.filter && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Traffic Filters</h3>

              {/* Geographic Filters */}
              {(campaign.filter.allowed_countries?.length > 0 || campaign.filter.blocked_countries?.length > 0) && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Geographic Targeting</label>
                  {campaign.filter.allowed_countries?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-600">Allow Only: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-1">
                        {campaign.filter.allowed_countries.map((code) => (
                          <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {campaign.filter.blocked_countries?.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600">Block: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-1">
                        {campaign.filter.blocked_countries.map((code) => (
                          <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Language Filters */}
              {(campaign.filter.allowed_languages?.length > 0 || campaign.filter.blocked_languages?.length > 0) && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Language Targeting</label>
                  {campaign.filter.allowed_languages?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-600">Allow Only: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-1">
                        {campaign.filter.allowed_languages.map((code) => (
                          <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {campaign.filter.blocked_languages?.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600">Block: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-1">
                        {campaign.filter.blocked_languages.map((code) => (
                          <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* OS Filters */}
              {(campaign.filter.allowed_os?.length > 0 || campaign.filter.blocked_os?.length > 0) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Operating System Targeting</label>
                  {campaign.filter.allowed_os?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-600">Allow Only: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-1">
                        {campaign.filter.allowed_os.map((os) => (
                          <span key={os} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                            {os}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {campaign.filter.blocked_os?.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-600">Block: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-1">
                        {campaign.filter.blocked_os.map((os) => (
                          <span key={os} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
                            {os}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* No Filters Message */}
              {!campaign.filter.allowed_countries?.length &&
               !campaign.filter.blocked_countries?.length &&
               !campaign.filter.allowed_languages?.length &&
               !campaign.filter.blocked_languages?.length &&
               !campaign.filter.allowed_os?.length &&
               !campaign.filter.blocked_os?.length && (
                <p className="text-xs text-gray-500 italic">No traffic filters configured</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* A/B Test Statistics */}
      {campaign.target_mode && campaign.target_mode !== 'default' && campaign.split_links && campaign.split_links.length > 0 && (
        <ABTestStats
          campaignId={campaign.id}
          fetchStats={(period) => getCampaignABStats(campaign.id, period)}
        />
      )}

      {/* Danger Zone */}
      {!isDeleted && (
        <Card>
          <h2 className="text-lg font-semibold text-red-600 mb-4">{t('detail.danger.title')}</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">{t('detail.danger.delete_title')}</h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('detail.danger.delete_description')}
              </p>
              <button onClick={handleDelete} className="btn btn-danger">
                {t('detail.danger.delete_button')}
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
